import { Box, Card, Flex, Grid, Text } from '@chakra-ui/react'
import React, { ReactNode, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import MasterTable, { Column, TableType } from '../tables/base/MasterTable'
import Pagination from '../tables/base/Pagination'
import SearchInput from '../tables/base/SearchInput'
import LoadingTable from '../tables/base/LoadingTable'
import type { TableState } from '../../hooks/useTableState'

interface TableLayoutProps<T extends Record<string, unknown>> {
  tableState: TableState<T>
  columns: Column[]
  tableType: TableType
  onRowClick?: (row: T) => void
  headerActions?: ReactNode
  emptyMessage?: string
  onCopyLink?: (row: T) => void
  onOpenNewTab?: (row: T) => void
  onSearch?: (row: T) => void
  onRowIntent?: (row: T) => void
  disableMobileRowClick?: boolean
  subHeader?: ReactNode
}

const TableLayout = React.forwardRef<
  HTMLDivElement,
  TableLayoutProps<any>
>(
  (
    {
      tableState,
      columns,
      tableType,
      onRowClick,
      headerActions,
      emptyMessage,
      onCopyLink,
      onOpenNewTab,
      onSearch,
      onRowIntent,
      disableMobileRowClick,
      subHeader,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const hasLoadedOnce = useRef(false)

    useEffect(() => {
      if (tableState.totalItems > 0) {
        hasLoadedOnce.current = true
      }
    }, [tableState.totalItems])

    const allSelected =
      tableState.data.length > 0 &&
      tableState.selectedRows.length === tableState.data.length

    const isIndeterminate =
      tableState.selectedRows.length > 0 && !allSelected

    const handleSelectAll = (isChecked: boolean) => {
      if (isChecked) {
        tableState.handleSelectAll(
          true,
          tableState.data.map((row) => row.id)
        )
      } else {
        tableState.clearSelection()
      }
    }

    return (
      <Card width="100%" p="0" ref={ref}>
        <Grid minW={0}>
          <Box minW={0}>
            {subHeader}
            {/* Header: Search + Actions Row */}
            <Flex
              justifyContent="space-between"
              alignItems={{ base: 'stretch', sm: 'center' }}
              direction={{ base: 'column', sm: 'row' }}
              height={{ base: 'auto', sm: '64px' }}
              flexWrap="wrap"
              gap={{ base: 3, sm: 4 }}
              px={{ base: '12px', sm: '20px' }}
              py={{ base: '12px', sm: 0 }}
              minW={0}
            >
              <SearchInput onSearch={tableState.setSearch} />
              {headerActions && <Flex gap="8px">{headerActions}</Flex>}
            </Flex>

            {/* Table Content */}
            {tableState.isLoading ? (
              <Box minH={{ base: 'auto', md: 'calc(100vh - 300px)' }}>
                <LoadingTable columns={columns} />
              </Box>
            ) : tableState.data.length === 0 ? (
              <Box minH={{ base: 'auto', md: 'calc(100vh - 300px)' }} display="flex" alignItems="center" justifyContent="center">
                <Text color="solid.500" fontSize="14px">
                  {emptyMessage || t('table.noData')}
                </Text>
              </Box>
            ) : (
              <Box minH={{ base: 'auto', md: 'calc(100vh - 300px)' }}>
                <MasterTable
                  columns={columns}
                  data={tableState.data}
                  onRowClick={onRowClick}
                  onRowIntent={onRowIntent}
                  onCopyLink={onCopyLink}
                  onOpenNewTab={onOpenNewTab}
                  onSearch={onSearch}
                  selectedRows={tableState.selectedRows}
                  onSelectAll={handleSelectAll}
                  onSelectRow={tableState.handleSelectRow}
                  allSelected={allSelected}
                  isIndeterminate={isIndeterminate}
                  tableType={tableType}
                  disableMobileRowClick={disableMobileRowClick}
                />
              </Box>
            )}

            {/* Pagination */}
            {(hasLoadedOnce.current || tableState.data.length > 0) && (
              <Pagination
                currentPage={tableState.page}
                totalItems={tableState.totalItems}
                itemsPerPage={tableState.itemsPerPage}
                maxPage={tableState.totalPages}
                handlePreviousPage={() => tableState.setPage(tableState.page - 1)}
                handleNextPage={() => tableState.setPage(tableState.page + 1)}
                handlePageClick={(page) => tableState.setPage(page)}
              />
            )}
          </Box>
        </Grid>
      </Card>
    )
  }
)

TableLayout.displayName = 'TableLayout'

export default TableLayout
